import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link as LinkIcon, FileText, Globe, ArrowRight, ChevronLeft, Plus, X, Loader2, Info } from 'lucide-react';
import { cn } from '../utils';
import { useActivities } from '../context/ActivitiesContext';

const ResourceCreation = () => {
  const { id: community_id } = useParams();
  const navigate = useNavigate();
  const { addResource } = useActivities();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('Link');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const TITLE_LIMIT = 100;
  const DESCRIPTION_LIMIT = 500;

  const handleCreate = async () => {
    if (!title.trim() || !url.trim()) {
      setError('Please provide both a title and a valid URL/Link.');
      return;
    }

    if (title.length > TITLE_LIMIT) {
      setError(`Title is too long (max ${TITLE_LIMIT} chars)`);
      return;
    }

    if (description.length > DESCRIPTION_LIMIT) {
      setError(`Description is too long (max ${DESCRIPTION_LIMIT} chars)`);
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await addResource({
        title,
        type,
        url,
        description,
        community_id,
        size: type === 'PDF' || type === 'Sheet' ? 'Unknown' : null
      });
      navigate(`/c/${community_id}/r`);
    } catch (err) {
      console.error('Failed to create resource', err);
      setError('Failed to add resource. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resourceTypes = [
    { name: 'Link', icon: LinkIcon, color: "text-indigo-600", bg: "bg-indigo-50" },
    { name: 'PDF', icon: FileText, color: "text-red-500", bg: "bg-red-50" },
    { name: 'Sheet', icon: FileText, color: "text-emerald-500", bg: "bg-emerald-50" },
    { name: 'Asset', icon: Globe, color: "text-purple-500", bg: "bg-purple-50" },
    { name: 'Other', icon: Info, color: "text-amber-500", bg: "bg-amber-50" },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
      <div className="flex items-center gap-4 mb-10">
        <button 
          onClick={() => navigate(-1)}
          className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Add Community Resource</h1>
          <p className="text-gray-500 font-medium text-sm">Share knowledge, tools, or documents with your hub.</p>
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
            <X size={16} />
            {error}
          </div>
        )}

        <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 space-y-6 shadow-xl shadow-gray-100/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Resource Title</label>
                  <span className={cn("text-[10px] font-bold", title.length > TITLE_LIMIT ? "text-red-500" : "text-gray-400")}>
                    {title.length}/{TITLE_LIMIT}
                  </span>
                </div>
                <input 
                  type="text" 
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, TITLE_LIMIT + 10))}
                  placeholder="e.g. Project Roadmap 2024"
                  className={cn(
                    "w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all text-lg",
                    title.length > TITLE_LIMIT && "ring-2 ring-red-500/20"
                  )}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">URL / Link</label>
                <input 
                  type="text" 
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium transition-all"
                />
              </div>
            </div>

            <div className="space-y-6">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Resource Type</label>
              <div className="grid grid-cols-2 gap-3">
                {resourceTypes.map((t) => (
                  <button
                    key={t.name}
                    onClick={() => setType(t.name)}
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left",
                      type === t.name ? "border-brand-indigo bg-brand-indigo/5" : "border-gray-50 bg-white hover:border-gray-100"
                    )}
                  >
                    <t.icon size={18} className={type === t.name ? t.color : "text-gray-400"} />
                    <span className="font-bold text-gray-900 text-sm">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Short Description (Optional)</label>
              <span className={cn("text-[10px] font-bold", description.length > DESCRIPTION_LIMIT ? "text-red-500" : "text-gray-400")}>
                {description.length}/{DESCRIPTION_LIMIT}
              </span>
            </div>
            <textarea 
              rows="4"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT + 50))}
              placeholder="What is this resource about?"
              className={cn(
                "w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium transition-all resize-none",
                description.length > DESCRIPTION_LIMIT && "ring-2 ring-red-500/20"
              )}
            ></textarea>
          </div>
        </div>

        <button 
          onClick={handleCreate}
          disabled={loading}
          className="w-full bg-brand-indigo text-white font-bold py-5 rounded-2xl shadow-xl shadow-brand-indigo/20 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all text-lg disabled:opacity-50"
        >
          {loading ? <Loader2 className="animate-spin" size={24} /> : (
            <>
              Add to Library
              <ArrowRight size={20} />
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
};

export default ResourceCreation;
